import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "clsx";
import { useAppDispatch, useAppSelector } from "store";

import styles from "../CmsFooterBrandCoreEditModal.module.scss";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import SwitchBtn from "components/buttons/SwitchBtn";
import { DraggableList } from "components/DraggableList/DraggableList";
import RoundBtn from "components/buttons/RoundBtn";
import { ImageUpload } from "components/imageUpload";

import { toggleArray } from "helpers/toggleArray";
import {
  FOOTER_BRANDCORE_COLUMNS,
  BrandCoreColumn,
  BrandCoreExtended,
  handleUpdateDraftItem,
} from "store/cms/footer/brandCore/slice";
import { selectFilteredGeo } from "store/geoFilter/selectors";
import { getGeoDropdownOptions } from "helpers/geoDropdown";

type Props = {
  onChangeLogo: (file: File) => void;
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  readOnly?: boolean;
};

const BrandCoreMainTab: React.FC<Props> = ({
  onChangeLogo,
  fieldErrors,
  clearFieldError,
  readOnly,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const geoList = useAppSelector(selectFilteredGeo);
  const brandCoreDraftItem = useAppSelector(
    (store) => store["cms-brandCore"].draftItem,
  );

  const draftItemColumnsIndexed = brandCoreDraftItem.columns.map((column) => ({
    id: column,
    title: column,
  }));

  const availableColumns = FOOTER_BRANDCORE_COLUMNS.map((column) => ({
    id: column,
    title: column,
  })).filter((column) => !brandCoreDraftItem.columns.includes(column.id));

  const [
    selectedAvailableBrandCoreColumns,
    setSelectedAvailableBrandCoreColumns,
  ] = useState<BrandCoreColumn[]>([]);

  const [selectSelectedBrandCoreColumns, setSelectSelectedBrandCoreColumns] =
    useState<BrandCoreColumn[]>([]);

  const handleChangeData = (data: Partial<BrandCoreExtended>) => {
    dispatch(handleUpdateDraftItem(data));
  };

  const onSelectAvailablePaymentMethod = (id: BrandCoreColumn) => {
    setSelectedAvailableBrandCoreColumns((c) => toggleArray(c, id));
    setSelectSelectedBrandCoreColumns([]);
  };

  const onSelectConnectedPaymentMethod = (id: BrandCoreColumn) => {
    setSelectSelectedBrandCoreColumns((c) => toggleArray(c, id));
    setSelectedAvailableBrandCoreColumns([]);
  };

  const onSortSelectedColumns = (sorted: typeof draftItemColumnsIndexed) => {
    handleChangeData({ columns: sorted.map((col) => col.id) });
  };

  const onAddPaymentMethod = (cols: string[]) => {
    const toAdd = FOOTER_BRANDCORE_COLUMNS.filter((c) => cols.includes(c));
    handleChangeData({
      columns: [...brandCoreDraftItem.columns, ...toAdd],
    });
    setSelectedAvailableBrandCoreColumns([]);
  };

  const onDeletePaymentMethod = (cols: string[]) => {
    handleChangeData({
      columns: brandCoreDraftItem.columns.filter((c) => !cols.includes(c)),
    });
    setSelectSelectedBrandCoreColumns([]);
  };

  const onChangeLogoLocal = (file: File) => {
    onChangeLogo(file);
    handleChangeData({ logo: "" });
    clearFieldError("file");
  };

  return (
    <div className={styles.content}>
      <div className={styles.action}>
        <div className={cn(styles.actionItem)}>
          <InnerInput
            value={brandCoreDraftItem?.title ?? ""}
            placeholder={t("enterTitle")}
            label={t("title")}
            type="text"
            id="text-main-title"
            onChange={(e) => {
              handleChangeData({ title: e });
              clearFieldError("title");
            }}
            hasError={Boolean(fieldErrors?.["title"])}
            disabled={readOnly}
          />
        </div>

        <div className={cn(styles.actionItem, styles.geoActionItem)}>
          <DefaultDropdown
            options={getGeoDropdownOptions(geoList)}
            label={t("geo")}
            value={brandCoreDraftItem?.geo || geoList[0]}
            onSelect={(t) => {
              handleChangeData({ geo: `${t}` });
              clearFieldError("geo");
            }}
            hasError={Boolean(fieldErrors?.["geo"])}
            disabled={readOnly}
          />
        </div>

        <div className={cn(styles.actionItem)}>
          <SwitchBtn
            activeTxt={t("active")}
            isActive={!!brandCoreDraftItem?.isActive}
            handleClick={() =>
              handleChangeData({
                isActive: brandCoreDraftItem?.isActive ? 0 : 1,
              })
            }
            disabled={readOnly}
          />
        </div>
      </div>

      <ImageUpload
        className={styles.imageUpload}
        image={brandCoreDraftItem.logoRef}
        onAddImage={onChangeLogoLocal}
        isActive={!!brandCoreDraftItem?.isActive}
        hasError={Boolean(fieldErrors?.["file"])}
        disabled={readOnly}
      />

      <div className={styles.brandCoreContent}>
        <div className={styles.draggableListWrapper}>
          <p className={styles.draggableListTitle}>{t("selected")}</p>

          <DraggableList
            // @ts-ignore
            items={draftItemColumnsIndexed}
            selectedItems={selectSelectedBrandCoreColumns}
            onSelect={onSelectConnectedPaymentMethod}
            onSort={onSortSelectedColumns}
            isDragRestricted={readOnly}
          >
            {draftItemColumnsIndexed.map((item, idx) => (
              <DraggableList.Item
                key={item.id}
                // @ts-ignore
                id={item.id}
                index={idx}
                className={styles.dndItem}
              >
                <div className={styles.dndItem}>{item.title}</div>
              </DraggableList.Item>
            ))}
          </DraggableList>
        </div>

        <div className={styles.addRemoveButtons}>
          {!readOnly && (
            <>
              <RoundBtn
                handleClick={() =>
                  onAddPaymentMethod(selectedAvailableBrandCoreColumns)
                }
                txt="+"
                disabled={!selectedAvailableBrandCoreColumns.length}
              />
              <RoundBtn
                handleClick={() =>
                  onDeletePaymentMethod(selectSelectedBrandCoreColumns)
                }
                txt="-"
                disabled={!selectSelectedBrandCoreColumns.length}
              />
            </>
          )}
        </div>

        <div className={styles.draggableListWrapper}>
          <p className={styles.draggableListTitle}>{t("available")}</p>

          <DraggableList
            // @ts-ignore
            items={availableColumns}
            selectedItems={selectedAvailableBrandCoreColumns}
            onSelect={(v) => onSelectAvailablePaymentMethod(`${v}`)}
            isDragRestricted
          >
            {availableColumns.map((item, idx) => (
              <DraggableList.Item
                key={item.id}
                // @ts-ignore
                id={item.id}
                index={idx}
                className={styles.dndItem}
              >
                <div className={styles.dndItem}>{item.title}</div>
              </DraggableList.Item>
            ))}
          </DraggableList>
        </div>
      </div>
    </div>
  );
};

export default BrandCoreMainTab;
